import React, { useEffect, useMemo, useState } from 'react'
import { Box, Grid, Typography, IconButton, Chip, LinearProgress } from '@material-ui/core'
import { Button, ConfirmationModal, PageTitle, SelectInput, Loading } from 'src/components'
import { useStyles } from './styles'
import { useGetBirthCertificateList } from 'src/hooks/api/birth-certificate/useGetBirthCertificateList'
import { useGetUserList } from 'src/hooks/api/user'
import { useAuth } from 'src/contexts/auth'
import { generateBirthCertificatePdf } from './components/BirthCertificatePdf'
import { EarringDto } from 'src/dto/birth-certificate.dto'
import { useUpdateEarring } from 'src/hooks/api/birth-certificate/mutations/updateEarring'
import CloseIcon from '@material-ui/icons/Close'
import PersonIcon from '@material-ui/icons/Person'
import QrCode2Icon from '@material-ui/icons/CropFree'
import InboxIcon from '@material-ui/icons/Inbox'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { UpdateEarringModal } from './components/UpdateEarringModal'

export const BirthForm: React.FC = () => {
  const { user: authUser } = useAuth()
  const [certificateSelected, setCertificateSelected] = useState<string>('')
  const [isPdfGenerating, setIsPdfGenerating] = useState<boolean>(false)
  const [selectedEarring, setSelectedEarring] = useState<EarringDto | null>(null)
  const [earringToDelete, setEarringToDelete] = useState<EarringDto | null>(null)

  const styles = useStyles()

  const {
    data: birthCertificateList,
    isLoading: isBirthCertificateListLoading,
    isError: isBirthCertificateListError,
    refetch: refetchBirthCertificateList,
    isRefetching: isBirthCertificateListRefetching
  } = useGetBirthCertificateList()

  const {
    data: userList,
    isLoading: isUserListLoading,
    isError: isUserListError,
    refetch: refetchUserList,
    isRefetching: isUserListRefetching
  } = useGetUserList()

  const isLoading = isBirthCertificateListLoading || isUserListLoading

  const isFetching = isBirthCertificateListRefetching || isUserListRefetching

  const isError = isBirthCertificateListError || isUserListError

  const isBusy = isLoading || isFetching

  const refetch = async () => {
    await Promise.all([refetchBirthCertificateList(), refetchUserList()])
  }

  const selectItem = useMemo(() => {
    if (!birthCertificateList || birthCertificateList.length === 0 || !userList || userList.length === 0) return null

    const certificate = birthCertificateList.find((c) => c.idFicha === certificateSelected) || birthCertificateList[0]
    const user = userList.find((u) => u.username === certificate.username)

    const fazenda = authUser?.listPropriedades?.find((p) => p.idFazenda === certificate.idFazenda)

    return {
      ...certificate,
      brincos:
        certificate.brincos?.filter((brinco) => brinco.acao !== 'Removido').sort((a, b) => a.ordem! - b.ordem!) || [],
      username: `${user?.nome || ''} ${user?.sobrenome || ''}`,
      nomeFazenda: fazenda?.descricao || ''
    }
  }, [certificateSelected, birthCertificateList, userList, authUser])

  const usageStats = useMemo(() => {
    if (!selectItem || !selectItem.brincos || selectItem.brincos.length === 0) {
      return { total: 0, used: 0, percentage: 0, isComplete: false }
    }

    const total = selectItem.brincos.length
    const used = selectItem.brincos.filter((b) => b.status?.toLowerCase() !== 'disponivel').length
    const percentage = (used / total) * 100
    const isComplete = used === total

    return { total, used, percentage, isComplete }
  }, [selectItem])

  const { mutateAsync: updateEarring, isPending: isDeletingEarring } = useUpdateEarring()

  const handleDeleteEarring = async () => {
    if (!earringToDelete) return
    try {
      await updateEarring({ ...earringToDelete, acao: 'Removido' })
      toastSyncSuccess('Brinco removido com sucesso!')
      setEarringToDelete(null)
      refetchBirthCertificateList()
    } catch {
      toast.error('Erro ao remover brinco.')
    }
  }

  async function handlePrintBrincos(certificate) {
    setIsPdfGenerating(true)
    const certificateForPrint = {
      ...certificate,
      brincos: certificate.brincos?.filter((b) => b.status?.toLowerCase() === 'disponivel') || []
    }
    await generateBirthCertificatePdf(certificateForPrint)
    setIsPdfGenerating(false)
  }

  useEffect(() => {
    if (selectItem) {
      setCertificateSelected(selectItem.idFicha)
    }
  }, [selectItem])

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <PageTitle title='Ficha de Nascimento'>
            {selectItem && selectItem?.brincos && selectItem.brincos.length > 0 && (
              <div className={styles.headerBtnContainer}>
                <Button
                  type='button'
                  outlined
                  isLoading={isPdfGenerating}
                  onClick={() => {
                    if (selectItem && !isPdfGenerating) {
                      handlePrintBrincos(selectItem)
                    }
                  }}
                >
                  Imprimir Brincos
                </Button>
              </div>
            )}
          </PageTitle>
        </div>
      </div>

      {isBusy || isError ? (
        <Box className={styles.loadingContainer}>
          <Loading refetch={refetch} isError={isError} />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {/* Left Column - Form */}
          <Grid item xs={12} lg={4}>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Dados da Ficha</h2>

              <Box className={styles.formContent}>
                <Box className={styles.formGroup}>
                  <SelectInput
                    label='Ficha de Origem'
                    showBy={['nome']}
                    onChange={(value) => setCertificateSelected(value.idFicha)}
                    selectBy='idFicha'
                    options={birthCertificateList || []}
                    value={certificateSelected || ''}
                  />
                </Box>

                <Box className={styles.formGroup}>
                  <Box className={styles.userInfoContainer}>
                    <PersonIcon className={styles.userIcon} />
                    <Box>
                      <Typography variant='caption' className={styles.userLabel}>
                        Usuário Responsável
                      </Typography>
                      <Typography variant='body2' className={styles.userName}>
                        {selectItem?.username || ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {selectItem && selectItem.brincos && selectItem.brincos.length > 0 && (
                <Box className={styles.progressSection}>
                  <Box className={styles.progressHeader}>
                    <Typography variant='caption' className={styles.progressLabel}>
                      Progresso
                    </Typography>
                    <Box className={styles.progressStats}>
                      {usageStats.isComplete ? (
                        <Box className={styles.completedBadge}>
                          <CheckCircleIcon className={styles.completedIcon} />
                          <Typography variant='caption' className={styles.completedText}>
                            Concluído
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant='caption' className={styles.progressCount}>
                          {usageStats.used}/{usageStats.total}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box className={styles.progressBarContainer}>
                    <LinearProgress
                      variant='determinate'
                      value={usageStats.percentage}
                      className={usageStats.isComplete ? styles.progressBarComplete : styles.progressBar}
                    />
                  </Box>
                </Box>
              )}
            </section>
          </Grid>

          {/* Right Column - Records List */}
          <Grid item xs={12} lg={8}>
            <section className={styles.section}>
              <Box className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Registros de Brincos</h2>
                <Typography variant='caption' className={styles.recordsCount}>
                  {selectItem?.brincos?.length || 0}{' '}
                  {selectItem?.brincos?.length === 1 ? 'Item encontrado' : 'Itens encontrados'}
                </Typography>
              </Box>

              <Box className={styles.recordsList}>
                {selectItem && selectItem.brincos && selectItem.brincos.length > 0 ? (
                  selectItem.brincos.map((brinco) => (
                    <Box key={brinco.idBrinco} className={styles.recordItem} onClick={() => setSelectedEarring(brinco)}>
                      <Box className={styles.recordIcon}>
                        <QrCode2Icon />
                      </Box>

                      <Box className={styles.recordInfo}>
                        <Typography variant='caption' className={styles.recordLabel}>
                          Número do brinco
                        </Typography>
                        <Typography variant='h6' className={styles.recordNumber}>
                          {brinco.numeroCompleto}
                        </Typography>
                      </Box>

                      <Box className={styles.recordMeta}>
                        <Box className={styles.recordTag}>
                          <Typography variant='caption' className={styles.recordLabel}>
                            RGN
                          </Typography>
                          <Typography variant='body2' className={styles.recordTagValue}>
                            {brinco.numeroSufixo}
                          </Typography>
                        </Box>

                        <Box className={styles.recordStatus}>
                          <Chip
                            label={brinco.status?.toLowerCase() === 'disponivel' ? 'Disponível' : 'Usado'}
                            size='small'
                            className={
                              brinco.status?.toLowerCase() === 'disponivel' ? styles.statusAvailable : styles.statusUsed
                            }
                          />
                        </Box>

                        <IconButton
                          size='small'
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation()
                            setEarringToDelete(brinco)
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box className={styles.emptyState}>
                    <Box className={styles.emptyStateContent}>
                      <InboxIcon className={styles.emptyStateIcon} />
                      <Typography variant='body2' className={styles.emptyMessage}>
                        Nenhum brinco adicionado
                      </Typography>
                      <Typography variant='caption' className={styles.emptyMessageSubtitle}>
                        Os brincos adicionados aparecerão aqui
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </section>
          </Grid>
        </Grid>
      )}

      <UpdateEarringModal
        isOpen={!!selectedEarring}
        earring={selectedEarring}
        onClose={() => setSelectedEarring(null)}
        onSuccess={() => refetchBirthCertificateList()}
      />
      <ConfirmationModal
        isOpen={!!earringToDelete}
        title='Remover Brinco'
        message={`Deseja remover o brinco ${earringToDelete?.numeroCompleto}?`}
        onConfirm={handleDeleteEarring}
        onCancel={() => setEarringToDelete(null)}
        isLoading={isDeletingEarring}
      />
    </>
  )
}
